import { useEffect, useMemo, useRef, useState } from 'react';
import { listTasks, type Task } from '../api/tasks.js';
import { useUi } from '../stores/ui.js';
import { useProjects } from '../stores/projects.js';
import { Input } from '../ui/Input.js';
import { Modal } from '../ui/Modal.js';
import { formatShort } from '../lib/dates.js';
import { teinte } from '../lib/teinte.js';
import { TexteLie } from './TexteLie.js';

export function SearchModal(): React.ReactElement | null {
  const open = useUi((s) => s.searchOpen);
  const close = useUi((s) => s.closeSearch);
  const openTaskDetail = useUi((s) => s.openTaskDetail);
  const projects = useProjects((s) => s.projects);

  const [query, setQuery] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedIdx(0);
    setLoading(true);
    listTasks('all')
      .then(setTasks)
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [open]);

  const matches = useMemo(() => {
    if (!query.trim()) return tasks.slice(0, 20);
    const q = query.toLowerCase();
    return tasks
      .filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          (t.description ?? '').toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [tasks, query]);

  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  if (!open) return null;

  function openTask(id: string): void {
    close();
    openTaskDetail(id);
  }

  function projectOf(projectId: string): {
    name: string;
    color: string;
  } | null {
    const p = projects.find((x) => x.projectId === projectId);
    return p ? { name: p.name, color: p.color } : null;
  }

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      anchor="top"
      aria-label="Search tasks"
      initialFocusRef={inputRef}
      header={
        <Input
          ref={inputRef}
          role="combobox"
          aria-expanded
          aria-controls="search-results"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSelectedIdx((i) => Math.min(i + 1, matches.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSelectedIdx((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const t = matches[selectedIdx];
              if (t) openTask(t.taskId);
            }
          }}
          placeholder="Search tasks…"
          aria-label="Search tasks"
        />
      }
    >
      {loading ? (
        <div className="vide">
          <span className="chargement" role="status" aria-label="Loading" />
        </div>
      ) : matches.length === 0 ? (
        <div className="vide">
          <p>{query ? 'No match.' : 'No tasks.'}</p>
        </div>
      ) : (
        <div className="liste" id="search-results" role="listbox" aria-label="Tasks">
          {matches.map((t, idx) => {
            const p = projectOf(t.projectId);
            return (
              // Un <div> et non un <button> : le titre peut porter un lien, et un <a> n'a pas sa
              // place dans un bouton. Le clavier passe par la saisie (flèches, Entrée).
              <div
                key={t.taskId}
                role="option"
                aria-selected={idx === selectedIdx}
                onClick={() => openTask(t.taskId)}
                onMouseEnter={() => setSelectedIdx(idx)}
                className="ligne ligne-cliquable"
              >
                {p && <span className="point" style={teinte(p.color)} />}
                <span className="ligne-corps">
                  <span className="ligne-titre">
                    <TexteLie text={t.title} />
                  </span>
                  <span className="ligne-detail">
                    {[p?.name, t.dueDate ? formatShort(t.dueDate) : 'No date']
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
